import React, { useState, useEffect } from 'react'
import Pagination from '../../components/Pagination'
import { FiEye, FiPlusCircle, FiCalendar } from 'react-icons/fi'
import DashboardHeader from '../../components/DashboardHeader'
import Spinner from '../../components/Spinner'
import { Tooltip } from '../../layout/sidebar'
import ServiceOrderFilter from '../../components/Filters/ServiceOrderFilter'
import ServiceOrderDetail from '../../components/Drawers/ServiceOrderDetail'
import DeleteModal from '../../components/DeleteModal'
import CreateOfflineAppointmentModal from '../../components/modals/CreateOfflineAppointmentModal'
import { useHistory, useLocation } from 'react-router-dom'
import api from '../../utils/api'
import { getOrderImage } from '../../utils/productImage'
import {
  canBookAppointment,
  orderForAppointment,
  getOrderSourceLabel,
  getAppointmentStatusLabel,
  getAppointmentStatusColor,
} from '../../utils/offlineOrders'
import {
  getOrderStatusLabel,
  isServiceOrder,
  normalizeOrderStatus,
  ORDER_STATUS,
} from '../../utils/orderStatus'
import { ErrorNotification, SuccessNotification } from '../../utils'

const PAGE_SIZE = 10

const ServiceOrders = () => {
  const [loading, setLoading] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [showDrawer, setShowDrawer] = useState(false)
  const history = useHistory()
  const location = useLocation()
  const [appointmentOrder, setAppointmentOrder] = useState(null)
  const [showAppointmentModal, setShowAppointmentModal] = useState(false)
  const [states, setStates] = useState({
    page: 1,
    page_size: PAGE_SIZE,
    search: "",
    date_range: [],
    status: "all",
    filter: "all"
  })
  const [orders, setOrders] = useState([])
  const [totalPages, setTotalPages] = useState(1)
  const [count, setCount] = useState(0)
  const [suggestions, setSuggestions] = useState([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Fetch orders from API
  const fetchOrders = async () => {
    setLoading(true)
    try {
      // Build query params
      const params = {
        page: states.page,
        page_size: states.page_size,
      }
      if (states.search) params.search = states.search
      if (states.status && states.status !== 'all') params.status = states.status
      if (states.date_range && states.date_range.length === 2) {
        params.start_date = states.date_range[0]
        params.end_date = states.date_range[1]
      }
      // Add other filters as needed
      const res = await api.get('service-orders/orders/', { params })
      setOrders(res.data.results || res.data || [])
      setCount(res.data.count || (res.data.results ? res.data.results.length : res.data.length))
      setTotalPages(res.data.total_pages || Math.ceil((res.data.count || res.data.length) / states.page_size))
    } catch (err) {
      setOrders([])
      setCount(0)
      setTotalPages(1)
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchOrders()
    // eslint-disable-next-line
  }, [states.page, states.page_size, states.search, states.status, JSON.stringify(states.date_range)])

  useEffect(() => {
    const openOrderId = location.state?.openOrderId
    if (!openOrderId) return

    const openOrder = async () => {
      try {
        const res = await api.get(`service-orders/orders/${openOrderId}/`)
        const order = res.data
        setSelectedOrder(order)
        setShowDrawer(true)
        if (location.state?.openAppointment && canBookAppointment(order)) {
          setAppointmentOrder(orderForAppointment(order))
          setShowAppointmentModal(true)
        }
      } catch {
        ErrorNotification('Could not load the new order.')
      } finally {
        history.replace({ pathname: location.pathname, state: {} })
      }
    }

    openOrder()
  }, [location.state?.openOrderId])

  // Fetch suggestions as user types (debounced, simple example)
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!states.search) {
        setSuggestions([])
        return
      }
      try {
        // Example: fetch order numbers matching the search
        const res = await api.get('service-orders/orders/', { params: { search: states.search, page_size: 5 } })
        const orderNumbers = (res.data.results || res.data || []).map(o => o.order_number)
        setSuggestions(orderNumbers)
      } catch {
        setSuggestions([])
      }
    }
    const timeout = setTimeout(fetchSuggestions, 300)
    return () => clearTimeout(timeout)
  }, [states.search])
  const handleChange = (e) => {
    setStates({ ...states, [e.target.name]: e.target.value })
  }

  const handleSearch = (searchValue) => {
    setStates((prev) => ({ ...prev, search: searchValue, page: 1 }))
  }

  const handleStatusChange = (status) => {
    setStates((prev) => ({ ...prev, status, page: 1 }))
  }

  const handleDateRangeChange = (date_range) => {
    setStates((prev) => ({ ...prev, date_range, page: 1 }))
  }

  const handlePageChange = (pageNum) => {
    setStates((prev) => ({ ...prev, page: pageNum }))
  }

  // Set only the search value
  const setSearchValue = (val) => setStates(prev => ({ ...prev, search: val, page: 1 }))

  // Reset all filters
  const handleReset = () => {
    setStates({
      page: 1,
      page_size: PAGE_SIZE,
      search: "",
      date_range: [],
      status: "all",
      filter: "all"
    })
  }

  const getDeleteErrorMessage = (error) => {
    const data = error?.response?.data
    if (!data) return 'Failed to delete order. Please try again.'
    if (typeof data === 'string') return data
    if (data.detail) return data.detail
    if (data.message) return data.message
    const firstKey = Object.keys(data)[0]
    if (firstKey && Array.isArray(data[firstKey])) return data[firstKey][0]
    return 'Failed to delete order. Please try again.'
  }

  const fetchOrderDetail = async (orderId) => {
    const res = await api.get(`service-orders/orders/${orderId}/`)
    return res.data
  }

  const openOrderDrawer = async (order) => {
    try {
      const detail = await fetchOrderDetail(order.id)
      setSelectedOrder(detail)
    } catch {
      setSelectedOrder(order)
    }
    setShowDrawer(true)
  }

  const openScheduleAppointment = async (order) => {
    try {
      const detail = order?.id ? await fetchOrderDetail(order.id) : order
      setAppointmentOrder(orderForAppointment(detail))
      setShowAppointmentModal(true)
    } catch {
      setAppointmentOrder(orderForAppointment(order))
      setShowAppointmentModal(true)
    }
  }

  const handleDeleteOrder = async () => {
    if (!selectedOrder?.id) return

    setDeleteLoading(true)
    try {
      await api.delete(`service-orders/orders/${selectedOrder.id}/`)
      SuccessNotification('Order deleted successfully')
      setShowDeleteModal(false)
      setShowDrawer(false)
      setSelectedOrder(null)
      fetchOrders()
    } catch (error) {
      ErrorNotification(getDeleteErrorMessage(error))
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleOrderUpdated = (updatedOrder) => {
    if (updatedOrder) {
      setSelectedOrder(updatedOrder)
      setOrders((prev) =>
        prev.map((item) => (item.id === updatedOrder.id ? { ...item, ...updatedOrder } : item))
      )
    }
    fetchOrders()
  }

  const getAppointmentDisplay = (order) => {
    if (!isServiceOrder(order)) {
      return { label: 'N/A', color: 'text-gray-400' }
    }
    if (normalizeOrderStatus(order.status) === ORDER_STATUS.APPOINTMENT_BOOKED) {
      return {
        label: 'Appointment Scheduled',
        color: getAppointmentStatusColor('appointment_booked'),
      }
    }
    return {
      label: getAppointmentStatusLabel(order.appointment_status, order),
      color: getAppointmentStatusColor(order.appointment_status),
    }
  }

  return (
    <>
      <CreateOfflineAppointmentModal
        show={showAppointmentModal}
        setShow={setShowAppointmentModal}
        order={appointmentOrder}
        onSuccess={() => {
          fetchOrders()
          setAppointmentOrder(null)
          if (selectedOrder?.id) {
            api.get(`service-orders/orders/${selectedOrder.id}/`).then((res) => {
              handleOrderUpdated(res.data)
            }).catch(() => {})
          }
        }}
      />

      {showDeleteModal && (
        <DeleteModal
          handleDelete={handleDeleteOrder}
          showConfirmModal={showDeleteModal}
          setShowConfirmModal={setShowDeleteModal}
          loading={deleteLoading}
          title={`order ${selectedOrder?.order_number || selectedOrder?.id || ''}`.trim()}
        />
      )}

      <ServiceOrderDetail
        isOpen={showDrawer}
        setIsOpen={setShowDrawer}
        order={selectedOrder}
        onOrderUpdated={handleOrderUpdated}
        onScheduleAppointment={openScheduleAppointment}
        onDelete={(order) => {
          setSelectedOrder(order)
          setShowDeleteModal(true)
        }}
      />

      <div className="w-full lg:px-10 px-2 h-full py-10">
        <div className="w-full space-y-6">
          {/* DASHBOARD HEADER */}
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between md:flex-row md:items-center md:justify-between mb-4">
            <DashboardHeader label="Orders" />

            <div className="flex flex-col md:flex-row gap-2">
              <button
                onClick={() => history.push('/admin/service-orders/record')}
                className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 rounded-md capitalize"
              >
                <FiPlusCircle size={20} />
                Record Offline Order
              </button>

              <button
                onClick={() => history.push("/admin/service-order/inventory")}
                className="border bg-white border-yellowcolor hover:border-yellowcolor/80 text-yellowcolor px-6 flex flex-row items-center capitalize gap-2 py-2 rounded-md dark:bg-dark"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 11V17C22 21 21 22 17 22H7C3 22 2 21 2 17V7C2 3 3 2 7 2H8.5C10 2 10.33 2.44 10.9 3.2L12.4 5.2C12.78 5.7 13 6 14 6H17C21 6 22 7 22 11Z" stroke="#FFB733" stroke-width="1.5" stroke-miterlimit="10" />
                  <path d="M8 2H17C19 2 20 3 20 5V6.38" stroke="#FFB733" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                View Inventory
              </button>

              {/* <button
                onClick={() => history.push("/admin/add-stock")}
                className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 rounded-md capitalize"
              >
                <FiPlusCircle size={20} />
                Add New Service
              </button> */}
            </div>
          </div>

          <ServiceOrderFilter
            states={states}
            setStates={setStates}
            handleChange={handleChange}
            searchValue={states.search}
            setSearchValue={setSearchValue}
            onSearch={handleSearch}
            suggestions={suggestions}
            hideTypeFilter={true}
            placeholder="Search by Order ID"
            onStatusChange={handleStatusChange}
            onDateRangeChange={handleDateRangeChange}
          />
          {/* Reset Button */}
          {states.search?.length > 0 && <div className="flex flex-row items-center gap-2 mb-2">
            <button
              onClick={handleReset}
              className="border border-gray-300 text-gray-700 bg-white px-4 py-1.5 rounded-md text-sm hover:bg-gray-100 dark:hover:bg-secondaryDark"
              type="button"
            >
              Reset
            </button>
          </div>}

          {loading ? (
            <div className="flex justify-center items-center h-full">
              <Spinner />
            </div>
          ) : (
            <div>
              <div className="my-5">
                <div className="flex flex-col">
                  <div className="-m-1.5 overflow-x-auto">
                    <div className="p-1.5 min-w-full inline-block align-middle">
                      <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white bg-clip-border rounded-lg shadow-sm overflow-hidden ">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-border-dark">
                          <thead className="bg-transparent dark:bg-secondaryDark">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500  capitalize hover:text-border-dark dark:hover:text-border-dark  ">Order ID</th>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500  capitalize hover:text-border-dark dark:hover:text-border-dark  ">Customer Name</th>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Amount(GH₵)</th>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Order Date</th>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Source</th>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Location</th>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Status</th>
                              <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Appointment</th>
                              <th scope="col" className="px-6 py-3 text-end text-sm font-light text-gray-500  capitalize hover:text-black ">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
                            {orders?.length > 0 ? orders?.map((order, index) => (
                              <tr key={order?.id} className="text-[13px]">
                                <td className="px-6 hover:bg-gray-50 dark:hover:bg-secondaryDark py-4 whitespace-nowrap text-sm font-medium text-gray-800 dark:text-neutral-200 flex flex-row items-center gap-2">
                                  <img
                                    src={getOrderImage(order)}
                                    alt={order?.items?.[0]?.product_name || 'Order item'}
                                    className="w-10 h-10 rounded-md object-cover bg-gray-100"
                                  />
                                  {order?.order_number || order?.id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {order?.customer_fullname}
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap font-light">
                                  GH₵ {parseFloat(order?.total_amount).toLocaleString()}
                                </td>

                                <td className="px-6 py-4 capitalize whitespace-nowrap font-light">
                                  {order?.created_at ? new Date(order?.created_at).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap font-light capitalize">
                                  <span className="inline-flex px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-secondaryDark text-gray-700 dark:text-gray-300">
                                    {getOrderSourceLabel(order)}
                                  </span>
                                </td>

                                <td className="px-6 py-4 capitalize whitespace-nowrap font-light">
                                  {order?.delivery_region || 'N/A'}
                                </td>

                                <td className="px-6 py-4 capitalize whitespace-nowrap font-light">
                                  {getOrderStatusLabel(order?.status)}
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap font-light">
                                  {(() => {
                                    const appointment = getAppointmentDisplay(order)
                                    return (
                                      <span
                                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${appointment.color}`}
                                      >
                                        {appointment.label}
                                      </span>
                                    )
                                  })()}
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap text-end font-light">
                                  <div className="flex flex-row items-center gap-4">
                                    <Tooltip text="View" position="top">
                                      <button
                                        type="button"
                                        onClick={() => openOrderDrawer(order)}
                                        className="">
                                        <FiEye size={16} />
                                      </button>
                                    </Tooltip>
                                    {canBookAppointment(order) && (
                                      <Tooltip text="Schedule appointment" position="top">
                                        <button
                                          type="button"
                                          onClick={() => openScheduleAppointment(order)}
                                          className="text-yellowcolor"
                                        >
                                          <FiCalendar size={16} />
                                        </button>
                                      </Tooltip>
                                    )}
                                    <Tooltip text="Delete" position="top">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setSelectedOrder(order)
                                          setShowDeleteModal(true)
                                        }}
                                      >
                                        <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M18 4.98307C15.225 4.70807 12.4333 4.56641 9.65 4.56641C8 4.56641 6.35 4.64974 4.7 4.81641L3 4.98307" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M7.58398 4.14102L7.76732 3.04935C7.90065 2.25768 8.00065 1.66602 9.40898 1.66602H11.5923C13.0007 1.66602 13.109 2.29102 13.234 3.05768L13.4173 4.14102" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M16.2077 7.61523L15.666 16.0069C15.5744 17.3152 15.4993 18.3319 13.1743 18.3319H7.82435C5.49935 18.3319 5.42435 17.3152 5.33268 16.0069L4.79102 7.61523" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M9.10742 13.75H11.8824" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M8.41602 10.416H12.5827" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      </button>
                                    </Tooltip>
                                    {/* <Tooltip text="Update" position="top">
                                      <button
                                        onClick={() => {
                                          setShowUpdateModal(true)
                                          setSelectedUser(order)
                                        }} className="">
                                        <RxPencil1 size={16} />
                                      </button>
                                    </Tooltip> */}
                                  </div>
                                </td>
                              </tr>
                            )) : (
                              <tr><td colSpan={9} className="text-center py-8 text-gray-400">No orders found.</td></tr>
                            )}
                          </tbody>

                        </table>
                        <Pagination
                          currentPage={states.page}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default ServiceOrders